import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Text} from '@chakra-ui/react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import isValidAbn from 'is-valid-abn'

import Input from '../Input'
import {useAuthProvider} from '../../contexts/authentication'
import {updateSupplierCustomerContactDetails} from '../../../api'

const customerDetailFormSchema = (abnRequired) => {
  const abnSchema = abnRequired
    ? Yup.string().required('Required')
    : Yup.string()

  return Yup.object().shape({
    abn: abnSchema,
    email: Yup.string().email('Email is invalid').required('Required'),
  })
}

const Form = ({customer, onClickSubmit, abnRequired}) => {
  const [invoiceCustomerDetailError, setInvoiceCustomerDetailError] =
    React.useState('')

  const {authenticityToken, updateAuthenticityToken} = useAuthProvider()
  const [haveSubmitted, setHaveSubmitted] = React.useState(false)

  const formik = useFormik({
    initialValues: {
      email: customer?.email || '',
      abn: customer?.abn || '',
    },
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const {authenticity_token: token} =
          await updateSupplierCustomerContactDetails({
            authenticityToken,
            id: customer.id,
            ...values,
          })
        updateAuthenticityToken(token)
        setHaveSubmitted(true)
        onClickSubmit()
      } catch (e) {
        setInvoiceCustomerDetailError(e.message)
      }
    },
    validate: (values) => {
      const errors = {}
      if (values.abn && !isValidAbn(values.abn)) {
        errors.abn = 'Invalid ABN'
      }

      return errors
    },
    validationSchema: customerDetailFormSchema(abnRequired),
  })

  const savedChanges = haveSubmitted && !formik.dirty

  return (
    <form onSubmit={formik.handleSubmit}>
      <Input
        name="email"
        placeholder="Email"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.email}
        errorMessage={formik.errors.email}
        isInvalid={formik.touched.email && formik.errors.email}
        mb="sm"
      />
      <Input
        name="abn"
        placeholder="ABN"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.abn}
        errorMessage={formik.errors.abn}
        isInvalid={formik.touched.abn && formik.errors.abn}
        mb="sm"
      />
      {invoiceCustomerDetailError && (
        <Box>
          <Text fontSize="sm" color="primary.errorred">
            Fail to update customer detail: {invoiceCustomerDetailError}
          </Text>
        </Box>
      )}
      <Box display="flex" mb="md">
        <Button
          variant="primary"
          type="submit"
          loadingText="Saving..."
          isDisabled={!formik.isValid || savedChanges}
          isLoading={formik.isSubmitting}
        >
          {savedChanges ? 'Saved' : 'Save'}
        </Button>
      </Box>
    </form>
  )
}

Form.defaultProps = {
  onClickSubmit: () => {},
}

Form.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    abn: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
  onClickSubmit: PropTypes.func,
  abnRequired: PropTypes.bool.isRequired,
}

export default Form
